import React, { Component } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import { StoryModel, StorySettingsType } from 'types/story';
import { PARAMS } from 'common/constants';
import { location } from 'common/utils/url-helper';
import { StoryFacade } from 'common/utils/facades/story-facade';
import { getIsEditor } from 'common/utils/get-is-editor';
import GlobalStyles from 'common/components/GlobalStyles';
import Loader from 'common/components/Loader';

import { parseURL } from 'common/utils';
import { EitherField } from 'types/utils';
import { CARD_ELEMENTS_ID, STORY_ROOT_ID } from 'client/constants/common';
import { StoryHistory } from 'client/utils';
import Fade from 'client/components/common/Fade';
import SelectionHint from 'client/components/common/SelectionHint';
import SelectionHintProvider from 'client/components/common/SelectionHint/SelectionHintProvider';
import ResponsiveProvider from 'client/components/common/ResponsiveProvider';
import { ApiEvents } from 'client/ApiEvents';
import Credit from 'client/components/common/Credit';
import User from 'client/components/common/User';
import StoryElements from './StoryElements';
import CardsManager from './CardsManager';
import EmbedingConnect from './EmbedingConnect';
import css from './Story.scss';
import StoryFetcher from './StoryFetcher';
import GoogleFonts from './GoogleFonts';
import EventProvider from './EventProvider';
import InactiveStory from './InactiveStory';

const ScrollbarFix = React.lazy(() => import('./ScrollbarFix'));

const { isEmbedDevPreview, isPreviewRoute, isPreview } = location.client;

type State = {
	story?: StoryModel;
	isReachedPlanLimit?: boolean;
	version: typeof StoryFacade.VERSIONS[keyof typeof StoryFacade.VERSIONS];
};

type Props = RouteComponentProps & { story?: StoryModel };

class Story extends Component<Props, State> {
	state: State = {
		isReachedPlanLimit: false,
		story: undefined,
		version: StoryFacade.VERSIONS.latest,
	};

	isEditor = getIsEditor();

	get story() {
		const story = this.state.story ?? {};
		return new StoryFacade(story as StoryModel, this.state.version);
	}

	onFetch = (state: EitherField<{ story: StoryModel; isReachedPlanLimit: boolean }>) => {
		if (state?.story) {
			const sHistory = new StoryHistory({ storyId: state.story.id });
			sHistory.clearLastPlay();
		}
		this.setState(state);
	};

	onStoryClick = () => {
		if (!this.story.storyId) {
			return;
		}

		ApiEvents.sendStartStoryEvent({
			organizationId: this.story.base.organizationId,
			storyId: this.story.base.id,
			cardId:
				new URLSearchParams(this.props.location.search).get(PARAMS.CARD_ID) ||
				this.story.dataByFirstCard.card?._id ||
				'',
			teamId: this.story.base.teamId,
			memberId: this.story.base.createdBy,
		});
	};

	storyComponents = () => {
		if (!this.state.story) {
			return null;
		}

		const { cardId } = parseURL(window.location.search).searchObject;
		const { card, step } = cardId ? this.story.getDataByCardId(cardId) : this.story.dataByFirstCard;
		const nextDefaultCard = step ? this.story.getNextDefaultCard(step) : undefined;
		const { mediaQuery } = this.story;
		const storyElements = this.story.data.elements;
		const storySymbols = this.story.symbols;
		const { isEditor } = this;
		const isEmbed = isEmbedDevPreview || (window.parent !== window && !isEditor && !isPreviewRoute);

		return (
			<ResponsiveProvider mediaQuery={mediaQuery}>
				<SelectionHintProvider isEditor={isEditor}>
					<EventProvider
						nextDefaultCardId={nextDefaultCard?._id ?? ''}
						isEditor={isEditor}
						events={card!.events} // FIXME: what if there is no card indeed?
						story={this.story}
					>
						<StoryElements elements={storyElements} symbols={storySymbols} isEditor={isEditor} />
						<div id={CARD_ELEMENTS_ID}>
							<CardsManager story={this.story} isEditor={isEditor} />
						</div>
					</EventProvider>
					{isEditor && <SelectionHint />}
					{(isEditor || isPreviewRoute) && (
						<>
							{!isEmbedDevPreview && <GlobalStyles styles="html { overflow-y: scroll; }" />}
							{/* custom fonts */}
							<GlobalStyles styles={generateFontFace(this.story.settings.fonts)} />
							{/* google */}
							<GoogleFonts fonts={this.story.settings.fonts} />
						</>
					)}
					<Credit isEmbed={isEmbed} />
				</SelectionHintProvider>

				{isPreview && (
					<React.Suspense fallback={null}>
						<ScrollbarFix />
					</React.Suspense>
				)}
			</ResponsiveProvider>
		);
	};

	render() {
		const { isReachedPlanLimit } = this.state;

		return (
			<div
				id={STORY_ROOT_ID}
				className={css.story}
				onClickCapture={isReachedPlanLimit ? undefined : this.onStoryClick}
			>
				{/* Get current story data */}
				<StoryFetcher
					story={this.props.story}
					onFetch={this.onFetch}
					history={this.props.history}
					location={this.props.location}
				/>

				{/* Story components (page content) */}
				{isReachedPlanLimit ? <InactiveStory /> : this.storyComponents()}

				{/* Connect to sdk script. Only for published story and embed preview. */}
				{!this.isEditor && (!isPreviewRoute || isEmbedDevPreview) && <EmbedingConnect />}

				{/* User info. Only published story. */}
				{!isReachedPlanLimit && !this.isEditor && !isPreviewRoute && <User />}

				{/* Preloader */}
				<Fade>
					{!this.state.story && !isReachedPlanLimit && (
						<div className={css.loader}>
							<Loader color="black" />
						</div>
					)}
				</Fade>
			</div>
		);
	}
}

function generateFontFace(fonts: StorySettingsType['fonts']) {
	if (!fonts) {
		return '';
	}

	return Object.values(fonts).reduce((memo, value) => {
		if (!value || value?.fontType === 'google') {
			return memo;
		}

		// eslint-disable-next-line no-param-reassign
		memo += `
				@font-face {
					font-family: '${value.fontFamily}';
					font-weight: ${value.weight};
					font-style: ${value.style.replace(/['"]+/g, '').toLowerCase()};
					src: url('${value.url}');
				}
			`;
		return memo;
	}, '');
}

export default Story;
